import { isInternalUser } from '@/app/navigation/access';
import type { RowAction } from '@/components/ui/data-table/types';
import type { Project, SessionUser } from '@certhon/models';
import type { QueryClient } from '@tanstack/react-query';
import { Archive, ArchiveRestore, Star, StarOff } from 'lucide-react';
import { patchProject } from './projects.api';
import { projectMutations } from './projects.queries';
import { executeMutation } from '@/utils/executeMutation';

export { adaptActionsForPanel } from '@/utils/adaptActionsForPanel';

/**
 * Get actions for a project - works for both row actions (table) and panel actions.
 *
 * Toggle pairs (Follow/Unfollow, Archive/Unarchive) are split into two distinct
 * set-state actions, each guarded by a `disabled` predicate so only the
 * applicable action shows on a given row. Bulk handlers iterate the per-project
 * mutation (`tier: 'sequential'`) — no batched project endpoint exists.
 *
 * Note: PDF report action is handled separately in the details panel via
 * TicketReportContext.
 */
export function getProjectActions(
  queryClient: QueryClient,
  user: Pick<SessionUser, 'recnum' | 'internal'> | null,
): RowAction<Project>[] {
  const actions: RowAction<Project>[] = [];

  // Following and archiving are internal operations (the API rejects them
  // for external accounts); externals get a read-only list.
  if (user && !isInternalUser(user)) {
    return actions;
  }
  const userId = user?.recnum ?? null;

  // Follow — applicable when user is NOT a follower.
  if (userId) {
    const followMutation = projectMutations.follow();
    const unfollowMutation = projectMutations.unfollow();

    actions.push({
      id: 'follow',
      icon: Star,
      label: 'Follow',
      disabled: (project) => project.users?.includes(userId) ?? false,
      hideWhenDisabled: true,
      onCalled: async (project) => {
        await executeMutation(followMutation, {
          projectId: project.recnum,
          userId,
        });
      },
      bulk: { tier: 'sequential' },
    });

    // Unfollow — applicable when user IS a follower.
    actions.push({
      id: 'unfollow',
      icon: StarOff,
      label: 'Unfollow',
      disabled: (project) => !(project.users?.includes(userId) ?? false),
      hideWhenDisabled: true,
      onCalled: async (project) => {
        await executeMutation(unfollowMutation, {
          projectId: project.recnum,
          userId,
        });
      },
      bulk: { tier: 'sequential' },
    });
  }

  // Archive — applicable to non-archived projects only.
  actions.push({
    id: 'archive',
    icon: Archive,
    label: 'Archive',
    disabled: (project) => !!project.archived,
    hideWhenDisabled: true,
    onCalled: async (project) => {
      await patchProject(project.recnum, { archived: true });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({
        queryKey: ['projects', String(project.recnum)],
      });
    },
    bulk: { tier: 'sequential' },
  });

  // Unarchive — applicable to archived projects only.
  actions.push({
    id: 'unarchive',
    icon: ArchiveRestore,
    label: 'Unarchive',
    disabled: (project) => !project.archived,
    hideWhenDisabled: true,
    onCalled: async (project) => {
      await patchProject(project.recnum, { archived: false });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({
        queryKey: ['projects', String(project.recnum)],
      });
    },
    bulk: { tier: 'sequential' },
  });

  return actions;
}
